import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import API from '../../../../services/API'
import useIsMobile from '../../../../helpers/useIsMobile'
import AmbuNetLogo from '../../../../assets/images/ambunetLogo.png'
import {
  accessLogActor,
  accessLogSummary,
  dayHeading,
  FILTERABLE_KINDS,
  kindMeta,
  normaliseKind,
} from '../accessLogs/accessLogPresentation'

const LogEntry = ({ log }) => {
  const meta = kindMeta(log.kind)
  const actor = accessLogActor(log)
  const { Icon } = meta

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" py={1}>
      <Box position="relative" flexShrink={0}>
        <Avatar
          src={actor.system ? AmbuNetLogo : actor.photograph}
          sx={{ width: 34, height: 34, borderRadius: 1.5 }}
        >
          {actor.external ? <meta.Icon fontSize="small" /> : actor.name?.[0]}
        </Avatar>
        <Box
          sx={{
            position: 'absolute',
            right: -6,
            bottom: -6,
            width: 20,
            height: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: meta.color === 'default' ? 'grey.500' : `${meta.color}.main`,
            color: '#fff',
            border: '2px solid #fff',
          }}
        >
          <Icon sx={{ fontSize: 12 }} />
        </Box>
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" fontWeight={700} noWrap>
            {actor.name}
          </Typography>
          {actor.external && (
            <Chip
              size="small"
              label="External"
              color="warning"
              variant="outlined"
              sx={{ borderRadius: 1, height: 18, fontSize: '0.68rem' }}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {accessLogSummary(log)}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        flexShrink={0}
        sx={{ pt: 0.25 }}
      >
        {moment(log.createdAt).format('HH:mm')}
      </Typography>
    </Stack>
  )
}

// Side panel with the full access history of one record. Props are unchanged
// from the old modal so the ePCR, paper and uninjured views all keep working.
const AccessLogs = ({
  epcrID,
  paperPCRID,
  uninjuredReportID,
  open,
  onClose,
  recordLabel,
}) => {
  const isPhone = useIsMobile('sm')
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [selectedKinds, setSelectedKinds] = useState([])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(false)
    API.post('/epcr/accesslogs/', { epcrID, paperPCRID, uninjuredReportID })
      .then((res) => {
        if (cancelled) return
        if (res.status === 200) setLogs(res.data ?? [])
        else setError(true)
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [open, epcrID, paperPCRID, uninjuredReportID])

  const counts = useMemo(
    () =>
      logs.reduce((accumulator, log) => {
        const kind = normaliseKind(log.kind)
        accumulator[kind] = (accumulator[kind] ?? 0) + 1
        return accumulator
      }, {}),
    [logs],
  )

  const filtered = selectedKinds.length
    ? logs.filter((log) => selectedKinds.includes(log.kind))
    : logs

  const groups = useMemo(() => {
    const byDay = new Map()
    filtered.forEach((log) => {
      const key = moment(log.createdAt).format('YYYY-MM-DD')
      if (!byDay.has(key)) byDay.set(key, [])
      byDay.get(key).push(log)
    })
    return [...byDay.entries()]
  }, [filtered])

  const toggleKind = (kind) =>
    setSelectedKinds((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind],
    )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: isPhone ? '100vw' : 520, maxWidth: '100vw' },
      }}
    >
      <Stack height="100%">
        <Box px={2} pt={2} pb={1.5} borderBottom={1} borderColor="divider">
          <Stack direction="row" alignItems="center" spacing={1}>
            <HistoryIcon color="anMainBlue" />
            <Typography variant="h6" fontWeight={700} flex={1}>
              Access log
            </Typography>
            {!loading && (
              <Chip
                size="small"
                label={`${logs.length} ${logs.length === 1 ? 'entry' : 'entries'}`}
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
            )}
            <IconButton size="small" onClick={onClose} aria-label="Close access log">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {recordLabel
              ? `Everyone who has opened, downloaded, shared or updated ${recordLabel}.`
              : 'Everyone who has opened, downloaded, shared or updated this record.'}
          </Typography>
          <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1.5}>
            {FILTERABLE_KINDS.filter((kind) => counts[kind]).map((kind) => {
              const meta = kindMeta(kind)
              const selected = selectedKinds.includes(kind)
              return (
                <Chip
                  key={kind}
                  size="small"
                  clickable
                  icon={<meta.Icon />}
                  label={`${meta.label} · ${counts[kind]}`}
                  color={selected ? meta.color : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => toggleKind(kind)}
                  sx={{ borderRadius: 1, fontWeight: 600 }}
                />
              )
            })}
          </Stack>
        </Box>

        <Box flex={1} overflow="auto" px={2} py={1.5}>
          {loading ? (
            <Stack spacing={1.5}>
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} variant="rounded" height={52} />
              ))}
            </Stack>
          ) : error ? (
            <Alert severity="error">
              Unable to load the access log. Please try again later.
            </Alert>
          ) : filtered.length === 0 ? (
            <Box textAlign="center" py={6}>
              <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={600} mt={1}>
                {logs.length ? 'Nothing matches those filters' : 'No access recorded yet'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {logs.length
                  ? 'Clear a filter above to see more.'
                  : 'Views, downloads and share-code activity will appear here.'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {groups.map(([day, entries]) => (
                <Box key={day}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {dayHeading(day)}
                  </Typography>
                  <Stack divider={<Box borderBottom={1} borderColor="divider" sx={{ opacity: 0.6 }} />}>
                    {entries.map((log) => (
                      <LogEntry key={log._id} log={log} />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Drawer>
  )
}

export default AccessLogs
